import React, { useMemo } from "react";
import { useT, Namespace, useDateFnsLocale } from "@racoon/i18n";
import { DataTable, Column, Hyperlink, type SortingState } from "@racoon/ui";
import { format } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import { useTableSettings } from "~/shared/hooks/useTableSettings";
import { PhotoReportModal } from "~/features/photo-report-modal/ui/photo-report.modal";
import { usePickupLocationCard } from "~/features/pickup-location-modal/hooks/use-pickup-location-card";
import {
  containerViewStatus,
  driverFullName,
  lastJobDriver,
  lastJobPhotoRank,
  lastKnownPickupLocation,
} from "../../model/container.filters";
import { SecondaryStatusBadge, CONTAINER_STATUS_LABEL_KEY } from "./secondary-status-badge.admin";

type Container = ConnectProto.Container;

// Figma "Дата обновления": "09:23 21 Сен 2026" — time first, then the date.
// `MMM` is a localized month name, so this cannot go through `fmt.dateTime`:
// that helper hardcodes the Russian date-fns locale and would print Russian
// months to English and Kazakh users.
const UPDATED_AT_PATTERN = "HH:mm dd MMM yyyy";

interface ContainersTableProps {
  containers: Container[];
  isLoading: boolean;
  selectedContainer?: Container | null;
  onRowSelect?: (container: Container | null) => void;
  /** Opens the container in the edit dialog; the ID cell is its link. */
  onEditClick?: (container: Container) => void;
  /** True while the next page loads. */
  loadingMore?: boolean;
  /** Loads the next page when the last row scrolls into view. */
  onEndReached?: () => void;
  /** Reports the column sort, which the page sends to the server. */
  onSortChange?: (sorting: SortingState) => void;
}

export const ContainersTable: React.FC<ContainersTableProps> = ({
  containers,
  isLoading,
  selectedContainer,
  onRowSelect,
  onEditClick,
  loadingMore,
  onEndReached,
  onSortChange,
}) => {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  const { selectedCompanyId } = useSelectedCompany();
  const { columnConfig, handleColumnConfigChange } = useTableSettings(
    "inventory",
    selectedCompanyId || undefined,
  );
  // A container's site opens as a card over the registry.
  const { openLocationById, pickupLocationCard } = usePickupLocationCard();

  const columns = useMemo<Column<Container>[]>(() => {
    // Every column exposes a primitive accessor (the string/number actually
    // shown). Column ids are kept from the previous layout ("locationId",
    // "lastJob") so saved column configs keep applying.
    // Sorted on the server (CONTAINER_SORT_BY); the accessor still picks the
    // first click's direction: ascending for text, descending for numbers.
    return [
      {
        id: "name",
        header: t(Namespace.Settings, "inventory.table.id"),
        key: "name",
        sortable: true,
        width: 88,
        align: "center",
        // `align` only reaches the header, so the body cell centers itself.
        // The link stops the click, so opening a container does not also
        // toggle the row's selection.
        cell: (info) => {
          const name = (info.getValue() as string) || "-";
          return (
            <div className="text-center text-sm">
              {onEditClick ? (
                <Hyperlink onOpen={() => onEditClick(info.row.original)}>{name}</Hyperlink>
              ) : (
                name
              )}
            </div>
          );
        },
      },
      {
        id: "status",
        header: t(Namespace.Settings, "inventory.table.status"),
        accessor: (row) => {
          const status = containerViewStatus(row);
          return status ? t(Namespace.Settings, CONTAINER_STATUS_LABEL_KEY[status]) : "";
        },
        sortable: true,
        width: 140,
        cell: (info) => {
          const status = containerViewStatus(info.row.original);
          return status ? <SecondaryStatusBadge status={status} /> : "-";
        },
      },
      {
        id: "locationId",
        header: t(Namespace.Settings, "inventory.table.lastKnownLocation"),
        // Preloaded on the container (and on its last job), so the cell fetches nothing.
        accessor: (row) => lastKnownPickupLocation(row)?.name ?? "",
        sortable: true,
        width: 260,
        cell: (info) => {
          const location = lastKnownPickupLocation(info.row.original);
          if (!location) return "-";
          // The preloaded site may be partial, so the card fetches the full one.
          return (
            <Hyperlink onOpen={() => openLocationById(location.id)}>
              {location.name || `#${location.id}`}
            </Hyperlink>
          );
        },
      },
      {
        id: "containerType",
        header: t(Namespace.Settings, "inventory.table.containerType"),
        accessor: (row) => row.type?.name ?? "",
        sortable: true,
        width: 160,
        cell: (info) => (info.getValue() as string) || "-",
      },
      {
        id: "volume",
        header: t(Namespace.Settings, "inventory.table.volume"),
        accessor: (row) => row.type?.volume ?? 0,
        sortable: true,
        width: 96,
        cell: (info) =>
          `${info.getValue() as number} ${t(Namespace.Settings, "inventory.table.volumeUnit")}`,
      },
      {
        id: "updatedAt",
        header: t(Namespace.Settings, "inventory.table.updatedAt"),
        accessor: (row) => fromTimestamp(row.updatedAt)?.getTime() ?? 0,
        sortable: true,
        width: 172,
        cell: (info) => {
          const time = info.getValue() as number;
          return time ? format(new Date(time), UPDATED_AT_PATTERN, { locale: dateFnsLocale }) : "-";
        },
      },
      {
        id: "driver",
        header: t(Namespace.Settings, "inventory.table.driver"),
        accessor: (row) => {
          const driver = lastJobDriver(row);
          return driver ? driverFullName(driver) : "";
        },
        sortable: true,
        width: 180,
        cell: (info) => (info.getValue() as string) || "-",
      },
      {
        // Preloaded on the container, so the cell fetches nothing.
        id: "lastJob",
        header: t(Namespace.Settings, "inventory.table.photo"),
        accessor: lastJobPhotoRank,
        sortable: true,
        width: 72,
        align: "center",
        cell: (info) => {
          const job = info.row.original.lastJob;
          return (
            <div className="flex justify-center">{job ? <PhotoReportModal job={job} /> : "-"}</div>
          );
        },
      },
    ];
  }, [t, dateFnsLocale, openLocationById, onEditClick]);

  // Stable across renders: DataTable scrolls to the selected row whenever these keys
  // change, and each page load re-renders, which would snap the list back to it.
  const selectedRowKeys = useMemo(
    () => (selectedContainer ? [String(selectedContainer.id)] : []),
    [selectedContainer],
  );

  return (
    <div className="rounded-lg bg-white overflow-hidden flex-1 min-h-0 flex flex-col">
      {pickupLocationCard}
      <DataTable
        className="flex-1 min-h-0"
        columns={columns}
        data={containers}
        loading={isLoading}
        loadingMore={loadingMore}
        onEndReached={onEndReached}
        manualSorting
        onSortChange={onSortChange}
        // An empty state inside the table, not instead of it: unmounting DataTable
        // would drop its sort while the page still thinks one is on.
        empty={
          <p className="p-8 text-center text-text-subtle">
            {t(Namespace.Settings, "inventory.table.empty")}
          </p>
        }
        rowKey={(row: Container) => String(row.id)}
        selectedRowKeys={selectedRowKeys}
        onRowClick={
          onRowSelect
            ? (row: Container) => {
                if (selectedContainer?.id === row.id) {
                  onRowSelect(null);
                } else {
                  onRowSelect(row);
                }
              }
            : undefined
        }
        columnConfig={columnConfig}
        onColumnConfigChange={handleColumnConfigChange}
        namespace={Namespace.Settings}
      />
    </div>
  );
};
